<template>
  <div class="document-container">
    <es-loader
      v-if="loading"
      style="margin-top: 15rem"
    />
    <es-exception
      v-else-if="loadingError"
      margin="15rem"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
    />
    <div v-else>
      <template v-if="$route.name === 'PidMasterRecord' || $route.name === 'DmMasterRecord'">
        <pid-retired-id-panel
          v-if="isRetired"
          :person="person"
        />
        <person-profile-master-person-details
          :isRetired="isRetired"
          :person="person"
          :userHasEditAccessToPersonLocation="userHasEditAccessToPersonLocation"
        />
        <person-profile-master-location-info
          v-for="(item, index) in personLocations"
          :key="index"
          :isRetired="isRetired"
          :person="person"
          :personLocations="item"
          :userHasEditAccessToPersonLocation="userHasEditAccessToPersonLocation"
          style="margin-top: 1rem"
        />
        <person-profile-master-notes
          v-if="hasModule('PRSID:PRPRF:NOTES:VIEW:GENERAL')
            || hasModule('PRSID:PRPRF:NOTES:VIEW:ADMIN')"
          :person="person"
          :isRetired="isRetired"
          style="margin-top: 1rem"
        />
        <div class="info-columns">
          <person-profile-master-matching-information
            :personMatchingInformation="personMatchingInformation"
          />
          <person-profile-master-transaction-information
            :personTransactionInformation="personTransactionInformation"
            :person="person"
          />
        </div>
      </template>
      <template v-else>
        <router-view name="personProfileSubBody"
          :isRetired="isRetired"
          :person="person"
          :userHasAccessToPersonLocation="userHasAccessToPersonLocation"
        />
      </template>
    </div>
  </div>
</template>


<script>
import axios from 'axios';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faUser,
  faPhoneRotary,
  faListAlt,
  faDatabase,
  faDiploma,
  faAddressBook,
} from '@fortawesome/pro-solid-svg-icons';
import {
  mapActions,
  mapGetters,
} from 'vuex';
import EsLoader from '@/components/EsLoader.vue';
import EsException from '@/components/EsException.vue';
import PersonProfileMasterPersonDetails from '@/views/PersonProfile/PersonProfileMasterPersonDetails.vue';
import PersonProfileMasterLocationInfo from '@/views/PersonProfile/PersonProfileMasterLocationInfo.vue';
import PersonProfileMasterNotes from '@/views/PersonProfile/PersonProfileMasterNotes.vue';
import PersonProfileMasterMatchingInformation from '@/views/PersonProfile/PersonProfileMasterMatchingInformation.vue';
import PersonProfileMasterTransactionInformation from '@/views/PersonProfile/PersonProfileMasterTransactionInformation.vue';
import PidRetiredIdPanel from '@/components/PidRetiredIdPanel.vue';
import FieldLabels from '@/mixins/FieldLabels';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faUser,
  faPhoneRotary,
  faListAlt,
  faDatabase,
  faDiploma,
  faAddressBook,
);

export default {
  mixins: [
    FieldLabels,
    PermissionManager,
  ],
  components: {
    EsLoader,
    EsException,
    PersonProfileMasterPersonDetails,
    PersonProfileMasterLocationInfo,
    PersonProfileMasterNotes,
    PersonProfileMasterMatchingInformation,
    PersonProfileMasterTransactionInformation,
    PidRetiredIdPanel,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    isRetired: {
      type: Boolean,
      default: false,
    },
    person: {
      type: Object,
      required: true,
    },
    userHasAccessToPersonLocation: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      loadingError: false,
      personLocations: [],
      personMatchingInformation: [],
      personTransactionInformation: {},
    };
  },
  async created() {
    await this.onLoad();
  },
  computed: {
    ...mapGetters('lookups', [
      'activeGenders',
      'activeStates',
      'activeRaces',
      'activeTitles',
    ]),
    userHasEditAccessToPersonLocation() {
      const { locationPermissions } = this.$store.state.user;

      // If there are no location permissions, means it has all districts and all schools access
      if (!locationPermissions.length) return true;

      // check if user has edit permission for this district and school
      // we look for a match on district and profile edit permission
      // AND... either no schools (all school access for district) or a match on school
      const list = locationPermissions.filter(lp => (
        !lp.districts.length || lp.districts.find(d => d.code === this.person.districtCode) != null)
        && (!lp.schools.length || lp.schools.find(d => d.code === this.person.schoolCode) != null)
        && lp.permissions.find(p => p === 'PRSID:PRPRF:PROFILE:EDIT') != null);
      return list && list.length > 0;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadGenders',
      'loadStates',
      'loadRaces',
      'loadGrades',
      'loadTitles',
    ]),
    async getPersonLocations() {
      if (this.isRetired) {
        return axios.get(`retired-person/${this.id}/location`);
      }
      return axios.get(`person/${this.id}/location`);
    },
    async getPersonMatchingInformation() {
      if (!this.hasModule('PRSID:PRPRF:PROFILE:VIEW')) return null;
      return axios.get(`personid/transactions/details?pidSubmissionId=${this.person.lastTranSerialNumber}&getDetails=true`);
    },
    async getPersonTransactionInformation() {
      return axios.get(`/personid/transactions/${this.person.lastTranSerialNumber}/transbatch`);
    },
    async onLoad() {
      this.loading = true;
      try {
        const res = await Promise.all([
          this.loadGenders(),
          this.loadStates(),
          this.loadRaces(),
          this.loadGrades(),
          this.loadTitles(),
          this.getPersonLocations(),
          this.getPersonMatchingInformation(),
          this.getPersonTransactionInformation(),
        ]);
        this.personLocations = this.groupByPersonType(res[5].data);
        // eslint-disable-next-line prefer-destructuring
        this.personMatchingInformation = res[6] ? res[6].data.data[0] : {};
        this.personTransactionInformation = res[7].data;
      } catch (err) {
        this.loadingError = true;
        console.log(err);
      } finally {
        this.loading = false;
      }
    },
    groupByPersonType(data) {
      const personLocations = data.reduce((acc, obj) => {
        const key = obj.personTypeName;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(obj);
        return acc;
      }, {});
      return personLocations;
    },
  },
  filters: {
  },
};
</script>

<style lang="sass" scoped>
.info-columns
  display: flex
  flex-wrap: wrap
  gap: 1rem
  margin-top: 1rem
.info-columns > *
  flex-grow: 1
  min-width: 0
</style>
